import { useMemo } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { CK, FONT_BODY, FONT_TITLE, statusChipSx } from '../../portalStyles';
import OperationProgress from '../OperationProgress';
import { IPAM_NEVER_READ_TITLE } from './ipamCopy';

// The cold-start state, shared by Dashboard, Address Space, Allocations and
// Public IPs.
//
// IPAM discovery is a periodic background sweep run by AWS, not a live query.
// Measured on the live spike: the FIRST sweep takes up to about 20 minutes after
// enabling, and a newly enrolled account appears in roughly 4-7 minutes. An
// "enabled, zero rows" tenant is therefore the NORMAL first experience, and it
// must not render as the ordinary empty state — "no resources found" on a
// correctly-enabled tenant reads as a failure and generates a support ticket for
// something that is working.
//
// It must not render as an ordinary *progress* state either. The one thing that
// distinguishes "we are still waiting" from "the RAM share was revoked and we
// will wait forever" is the per-association ResourceDiscoveryStatus, so the
// association chips are the load-bearing part of this component, not decoration.
//
// Deliberately PURE: it takes `onCheck` as a prop rather than importing the API
// client, so all four pages own their own polling and this stays testable.

// 30 s, not the shared 3 s POLL_INTERVAL_MS. Polling a ~20-minute AWS sweep every
// three seconds is roughly 400 pointless requests per tenant per cold start.
export const IPAM_DISCOVERY_POLL_MS = 30000;

export const IPAM_COLD_START_TITLE = 'AWS is still taking its first inventory';

export const IPAM_COLD_START_BODY =
  'Prism associated every account’s resource discovery and AWS accepted them. ' +
  'Discovery is a periodic background sweep, not a live query — the first sweep ' +
  'can take up to about 20 minutes after enabling, and a newly added account ' +
  'typically appears within 4–7 minutes. Nothing is wrong and there is nothing to retry.';

// THE THIRD STATE, AND THE REASON THIS COMPONENT TAKES `lastSweepAt` AT ALL.
//
// `firstDiscoveryAt == null` is the ONLY discriminator the four pages have, and it
// collapses two different facts:
//
//   - AWS has swept and has nothing yet — the cold window above. A statement
//     about AWS, and it is true.
//   - PRISM HAS NEVER SUCCESSFULLY READ AWS. Nothing here is a statement about
//     AWS at all, and the copy above is then a confident falsehood: live on
//     2026-08-22 AWS held 14 discovered CIDRs and had finished its sweep half an
//     hour earlier, while this panel told the operator AWS was still working and
//     that there was "nothing to retry".
//
// `last_discovery_refresh_at` is stamped by writeRefreshFreshness on EVERY
// completed refresh, whether or not it read a row, so its absence is the exact
// "Prism has not looked" signal — and it is a server timestamp, not a client
// guess. An unread state must never render as a claim about AWS's state.
// SPELLED ONCE, IN ipamCopy.js. The Address space zero state reaches this same
// claim from a different discriminator — the per-cache scope/discovered stamps
// rather than this panel's tenant-wide refresh clock — and two definitions of one
// sentence is exactly the drift that module exists to stop. Re-exported because
// this file was its original home.
export { IPAM_NEVER_READ_TITLE };

export const IPAM_NEVER_READ_BODY =
  'No inventory refresh has completed for this tenant, so nothing on this page is ' +
  'a statement about what AWS holds — Prism has not asked yet. AWS may well have ' +
  'finished its own discovery sweep already. Run a refresh; if it keeps completing ' +
  'without reading anything, the refresh itself is what needs looking at, not AWS.';

// relativeTime returns a short "2 min ago" style string for a past ISO time.
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} h ago`;
  return `${Math.round(diffSec / 86400)} d ago`;
};

// AWS reports exactly two values here. `not-found` is not a variant of waiting:
// it means the member's resource discovery was deleted or its RAM share was
// revoked, so that account will never appear however long the operator waits.
// Anything unrecognised is drawn as a warning rather than folded into either
// bucket, because guessing in this component is how a revoked share ends up
// looking healthy.
export function discoveryChipIntent(status) {
  if (status === 'active') return 'success';
  if (status === 'not-found') return 'error';
  return 'warning';
}

export default function IpamDiscoveryColdStart({
  associations = [],
  enabledAt,
  lastCheckedAt,
  // lastSweepAt is config.lastDiscoveryRefreshAt — the SERVER's record of the last
  // refresh that ran to completion. Absent means Prism has never read AWS, which is
  // a different fact from "AWS has nothing yet" and gets different copy. Undefined
  // (a caller that has not been updated) is treated as absent on purpose: the
  // honest state is the safe default.
  lastSweepAt = null,
  onCheck,
  checking = false,
}) {
  const enabledRel = relativeTime(enabledAt);
  const checkedRel = relativeTime(lastCheckedAt);
  const sweepRel = relativeTime(lastSweepAt);
  // NOT a truthiness test on lastSweepAt alone: an unparseable timestamp is also
  // "Prism cannot say it has read AWS".
  const neverRead = sweepRel === null;

  // Sorted so the broken associations are the first thing read, not something
  // the operator has to find among forty healthy chips.
  const ordered = useMemo(() => {
    const rank = (a) => (discoveryChipIntent(a?.status) === 'success' ? 1 : 0);
    return [...associations].sort((a, b) => rank(a) - rank(b));
  }, [associations]);

  return (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${CK.blue}14`,
          color: CK.blue,
        }}
      >
        <TravelExploreIcon />
      </Box>

      <Typography
        sx={{ fontFamily: FONT_TITLE, fontWeight: 600, fontSize: '1.1rem', color: CK.navy, mb: 1 }}
      >
        {neverRead ? IPAM_NEVER_READ_TITLE : IPAM_COLD_START_TITLE}
      </Typography>

      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          color: CK.muted,
          maxWidth: 640,
          mx: 'auto',
          mb: 3,
        }}
      >
        {neverRead ? IPAM_NEVER_READ_BODY : IPAM_COLD_START_BODY}
      </Typography>

      {/* Indeterminate is honest here and nowhere else in this capability: AWS
          exposes no progress for the sweep, and a fabricated percentage would be
          the one number on the page that is invented.

          It is HIDDEN when Prism has never read AWS: a progress bar labelled
          "waiting for the first discovery sweep" asserts that something is under
          way on AWS's side, which is precisely the claim this state cannot make. */}
      {!neverRead && (
        <Box sx={{ maxWidth: 420, mx: 'auto', mb: 3 }}>
          <OperationProgress percent={null} label="Waiting for the first discovery sweep" />
        </Box>
      )}

      {ordered.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ justifyContent: 'center', maxWidth: 720, mx: 'auto', mb: 3 }}
        >
          {ordered.map((a) => (
            <Chip
              key={`${a.accountId}:${a.region}`}
              size="small"
              label={`${a.accountId} · ${a.region}`}
              sx={statusChipSx(discoveryChipIntent(a.status))}
            />
          ))}
        </Stack>
      )}

      <Button
        variant="outlined"
        size="small"
        startIcon={<RefreshIcon />}
        onClick={onCheck}
        disabled={checking || !onCheck}
        sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
      >
        {checking ? 'Checking…' : 'Check for results'}
      </Button>

      {/* "last checked" is a CLIENT timestamp — when this page last reloaded — and
          on its own it reads as "Prism looked at AWS just now", which is what made
          the falsehood above so convincing. The server's own sweep time is printed
          beside it, and its ABSENCE is printed too rather than omitted. */}
      <Typography
        sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted, mt: 1.5 }}
      >
        {enabledRel ? `Enabled ${enabledRel}` : 'Enabled'}
        {checkedRel ? ` · page refreshed ${checkedRel}` : ''}
        {sweepRel ? ` · Prism last read AWS ${sweepRel}` : ' · Prism has never read AWS'}
      </Typography>
    </Box>
  );
}
